/* eslint-disable @angular-eslint/component-selector */
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControlStatus } from '@angular/forms';
import { CustomValidationError } from '@core/interfaces';
import { SafeHtmlPipe } from '@shared/pipes';
import { filter } from 'rxjs';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import { DateUtil } from '@core/utils';

@Component({
  selector: '[validationControl]',
  standalone: true,
  imports: [SafeHtmlPipe, NgClass, TranslocoPipe],
  template: `
    @if (errorMessage; as error) {
      @if (errorMessage?.customMessage === null) {
        <span [ngClass]="customCss" [innerHTML]="error.key | transloco: error.configuration | safeHtml"> </span>
      } @else {
        <span [ngClass]="customCss" [innerHTML]="errorMessage?.customMessage | safeHtml"> </span>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideTranslocoScope('validators')],
})
export class ValidationControlComponent {
  /**
   * The form control to validate.
   */
  private control: AbstractControl;

  /**
   * ChangeDetectorRef for triggering change detection.
   */
  private readonly cdRef = inject(ChangeDetectorRef);

  /**
   * DestroyRef for cleaning up subscriptions.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Sets the control to be validated and subscribes to its status and value changes.
   * Triggers change detection on changes.
   */
  @Input() set validationControl(control: AbstractControl) {
    this.control = control;

    // Subscribe to status changes and trigger change detection if invalid
    this.control.statusChanges
      .pipe(
        filter((status: FormControlStatus) => status === 'INVALID'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.cdRef.markForCheck());

    // Subscribe to value changes and trigger change detection
    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdRef.markForCheck());
  }

  /**
   * Custom messages for validation errors.
   * Example: { 'required': 'custom.translation.key' }
   */
  @Input() customMessages: Record<string, string> = {};

  /**
   * Custom CSS classes to apply to the error message.
   */
  @Input() customCss: string[];

  /**
   * Returns the current validation error message, or null if no errors.
   * If a custom message is provided, it is used; otherwise, a default translation key is used.
   */
  get errorMessage(): CustomValidationError | null {
    if (!this.control.errors) {
      return null;
    }

    const errors = Object.keys(this.control.errors);

    return {
      key: this.customMessages[errors[0]] ?? 'validators.' + errors[0],
      customMessage: this.customMessages[errors[0]] ?? null,
      configuration: {
        boldSuffix: '<b>',
        boldPrefix: '</b>',
        ...this.formatErrorValues(this.control.errors[errors[0]]),
      },
    };
  }

  private formatErrorValues(errorValue: Record<string, unknown>): Record<string, unknown> {
    if (!errorValue || typeof errorValue !== 'object') {
      return errorValue;
    }

    return Object.fromEntries(
      Object.entries(errorValue).map(([key, value]) => [
        key,
        value instanceof Date ? DateUtil.formatDate(value) : value,
      ]),
    );
  }
}
